import { createApiClient, createSessionClient, type ApiClient, type SessionClient } from '../http';
import { activityHeaders, createClientSession, type ClientSession } from '../tracking';
import { createScopeStore, type ScopeStore } from './scope-store';

/**
 * Everything an authenticated app needs outside React, created once at startup (main.tsx):
 *
 * - `api`     — the fetch core: session cookies included, activity headers on our own API;
 * - `client`  — `api` as the logged-in user: CSRF, `?orgNodeId=`/`?viewMode=` from `scope`,
 *               one refresh-and-retry on 401;
 * - `scope`   — the selected org node;
 * - `activity`— the tab's activity session (sessionStorage), reset on logout.
 *
 * Hand it to `<PlatformProvider>`.
 */
export interface PlatformSession {
  api: ApiClient;
  client: SessionClient;
  scope: ScopeStore;
  activity: ClientSession;
  /** Subscribe to "the session could not be renewed". Returns the unsubscribe. */
  onSessionExpired(listener: () => void): () => void;
}

export interface PlatformSessionOptions {
  apiBaseUrl: string;
  apiVersion: string;
  timeoutMs: number;
  /** Defaults to `localStorage`; tests pass their own. */
  storage?: Storage;
}

export function createPlatformSession(options: PlatformSessionOptions): PlatformSession {
  const activity = createClientSession({ persist: true });
  const scope = createScopeStore(options.storage);
  const expiredListeners = new Set<() => void>();
  const api = createApiClient({
    apiBaseUrl: options.apiBaseUrl,
    apiVersion: options.apiVersion,
    timeoutMs: options.timeoutMs,
    credentials: 'include',
    headers: () => activityHeaders(activity),
  });
  const client = createSessionClient(api, {
    scope: () => scope.requestScope(),
    onSessionExpired: () => expiredListeners.forEach(listener => listener()),
  });

  return {
    api,
    client,
    scope,
    activity,
    onSessionExpired(listener) {
      expiredListeners.add(listener);
      return () => expiredListeners.delete(listener);
    },
  };
}
